import type { Category } from './categories';

export type GuidedQuestion = {
  id: string;
  label: string;
  type: 'text' | 'select' | 'chips';
  options?: string[];
  placeholder?: string;
  helperText?: string;
};

// Shared by both fence slugs (fencing + fence_deck).
const FENCE_DECK_QUESTIONS: GuidedQuestion[] = [
  { id: 'fence_type', label: 'Fence / deck type', type: 'select',
    options: ['Wood fence', 'Vinyl fence', 'Chain-link', 'Aluminum / metal', 'Deck (wood)', 'Deck (composite)', 'Not sure'] },
  { id: 'fence_size', label: 'Approximate size', type: 'text', placeholder: 'e.g. ~150 linear feet, or 12x16 deck' },
  { id: 'fence_scope', label: 'New or repair?', type: 'select', options: ['New build', 'Repair / replace existing', 'Not sure'] },
];

// Keyed to the exact slugs in CATEGORY_OPTIONS. The Record<Category | '_generic'>
// type forces every category to be present and spelled correctly.
export const CATEGORY_QUESTIONS: Record<Category | '_generic', GuidedQuestion[]> = {

  // --- Automotive ---
  auto_repair: [
    { id: 'veh', label: 'Vehicle (year, make, model)', type: 'text', placeholder: 'e.g. 2018 Honda Civic' },
    { id: 'problem', label: "What's wrong?", type: 'text', placeholder: "e.g. won't start, grinding when braking" },
    { id: 'drivable', label: 'Is it drivable right now?', type: 'select', options: ['Yes', 'No', 'Not sure'] },
    { id: 'lights', label: 'Warning lights on?', type: 'text', placeholder: 'e.g. check engine, none' },
  ],
  auto_body: [
    { id: 'veh', label: 'Vehicle (year, make, model)', type: 'text', placeholder: 'e.g. 2020 Ford F-150' },
    { id: 'damage', label: 'Type of damage', type: 'chips', options: ['Dent', 'Scratch / paint', 'Collision', 'Rust', 'Bumper', 'Glass'] },
    { id: 'insurance', label: 'Through insurance?', type: 'select', options: ['Yes', 'No', 'Not sure'] },
  ],
  auto_parts: [
    { id: 'veh', label: 'Vehicle (year, make, model)', type: 'text', placeholder: 'e.g. 2015 Toyota Camry' },
    { id: 'part', label: 'Part needed', type: 'text', placeholder: 'e.g. alternator, brake pads' },
    { id: 'pref', label: 'New, used, or OEM?', type: 'select', options: ['New', 'Used', 'OEM', 'No preference'] },
  ],
  transmission: [
    { id: 'veh', label: 'Vehicle (year, make, model)', type: 'text', placeholder: 'e.g. 2016 Nissan Altima' },
    { id: 'type', label: 'Automatic or manual?', type: 'select', options: ['Automatic', 'Manual', 'Not sure'] },
    { id: 'symptoms', label: 'Symptoms', type: 'chips', options: ['Slipping', "Won't shift", 'Leaking fluid', 'Noise', 'Warning light', 'Other'] },
    { id: 'drivable', label: 'Is it drivable?', type: 'select', options: ['Yes', 'No', 'Not sure'] },
  ],
  oil_change: [
    { id: 'veh', label: 'Vehicle (year, make, model)', type: 'text', placeholder: 'e.g. 2019 Chevy Equinox' },
    { id: 'oil', label: 'Oil type preference', type: 'select', options: ['Conventional', 'Synthetic blend', 'Full synthetic', 'Not sure'] },
    { id: 'where', label: 'Mobile or shop?', type: 'select', options: ['Either', 'Mobile (come to me)', 'Shop'] },
  ],
  tire_service: [
    { id: 'veh', label: 'Vehicle (year, make, model)', type: 'text', placeholder: 'e.g. 2017 Honda Accord' },
    { id: 'service', label: 'Service needed', type: 'chips', options: ['New tires', 'Rotation', 'Flat repair', 'Balancing', 'Alignment'] },
    { id: 'size', label: 'Tire size (if known)', type: 'text', placeholder: 'e.g. 225/65R17, or not sure' },
  ],
  towing: [
    { id: 'veh', label: 'Vehicle (year, make, model)', type: 'text', placeholder: 'e.g. 2014 Jeep Wrangler' },
    { id: 'from', label: 'Pickup location', type: 'text', placeholder: 'e.g. address or cross streets' },
    { id: 'to', label: 'Where to?', type: 'text', placeholder: 'e.g. shop, home, dealership' },
    { id: 'reason', label: 'Reason', type: 'chips', options: ['Breakdown', 'Accident', 'Flat tire', 'Out of gas', "Won't start", 'Other'] },
  ],

  // --- Home trades & construction ---
  plumbing: [
    { id: 'plumb_issue', label: "What's the issue?", type: 'chips', options: ['Leak', 'Clog / drain', 'No hot water', 'Install / replace fixture', 'Running toilet', 'Other'] },
    { id: 'plumb_location', label: 'Where in the home?', type: 'text', placeholder: 'e.g. kitchen sink, upstairs bathroom' },
    { id: 'plumb_shutoff', label: 'Is the water shut off?', type: 'select', options: ['Yes', 'No', 'Not sure'], helperText: 'For an active leak, shutting off the water can prevent damage.' },
  ],
  electrical: [
    { id: 'elec_type', label: 'Type of work', type: 'chips', options: ['Outlet / switch', 'Lighting / fan', 'Panel / breaker', 'Wiring', 'EV charger', 'Other'] },
    { id: 'elec_safety', label: 'Anything unsafe or without power right now?', type: 'select', options: ['Yes', 'No'], helperText: 'If you smell burning or see sparks, please call an emergency electrician.' },
    { id: 'elec_location', label: 'Where in the home?', type: 'text', placeholder: 'e.g. garage, kitchen, whole house' },
  ],
  hvac: [
    { id: 'hvac_system', label: 'System type', type: 'select', options: ['Central AC', 'Heat pump', 'Furnace', 'Mini-split', 'Not sure'] },
    { id: 'hvac_problem', label: "What's happening?", type: 'chips', options: ['Not cooling', 'Not heating', 'Maintenance / tune-up', 'New install / replace', 'Strange noise', 'Other'] },
    { id: 'hvac_size', label: 'Home size', type: 'text', placeholder: 'e.g. ~2,000 sq ft, 3 bedrooms' },
    { id: 'hvac_age', label: 'Unit age (if known)', type: 'text', placeholder: 'e.g. ~10 years, or not sure' },
  ],
  electronics: [
    { id: 'device', label: 'Device / system', type: 'text', placeholder: 'e.g. TV mount, home theater, security cameras' },
    { id: 'work', label: 'Install, repair, or setup?', type: 'select', options: ['Install', 'Repair', 'Setup / configuration'] },
    { id: 'model', label: 'Brand / model (if known)', type: 'text', placeholder: 'e.g. Samsung 65", Ring' },
  ],
  roofing: [
    { id: 'roof_material', label: 'Roof material', type: 'select', options: ['Asphalt shingle', 'Metal', 'Tile', 'Flat / built-up', 'Not sure'] },
    { id: 'roof_size', label: 'Approximate size', type: 'text', placeholder: 'e.g. ~1,800 sq ft, or 1-story house' },
    { id: 'roof_leaking', label: 'Is it leaking now?', type: 'select', options: ['Yes', 'No', 'Not sure'] },
    { id: 'roof_scope', label: 'New roof or repair?', type: 'select', options: ['Full replacement', 'Repair', 'Not sure'] },
  ],
  gutters: [
    { id: 'gutter_work', label: 'What do you need?', type: 'chips', options: ['New gutters', 'Replace existing', 'Repair', 'Cleaning', 'Gutter guards', 'Downspouts'] },
    { id: 'gutter_size', label: 'Approximate length', type: 'text', placeholder: 'e.g. ~150 linear feet, or 1-story house' },
    { id: 'gutter_stories', label: 'How many stories?', type: 'select', options: ['1 story', '2 stories', '3+ stories'] },
  ],
  siding: [
    { id: 'siding_material', label: 'Siding material', type: 'select', options: ['Vinyl', 'Fiber cement (Hardie)', 'Wood', 'Metal', 'Brick / stone veneer', 'Not sure'] },
    { id: 'siding_size', label: 'Approximate area', type: 'text', placeholder: 'e.g. whole house, ~1,800 sq ft, or one side' },
    { id: 'siding_scope', label: 'New install or replace?', type: 'select', options: ['New install', 'Replace existing', 'Repair', 'Not sure'] },
  ],
  awnings: [
    { id: 'awning_type', label: 'Awning type', type: 'select', options: ['Patio / deck cover', 'Window awning', 'Door awning', 'Carport', 'Retractable', 'Not sure'] },
    { id: 'awning_size', label: 'Approximate size', type: 'text', placeholder: 'e.g. 12x16 patio, 3 windows' },
    { id: 'awning_scope', label: 'New install or repair?', type: 'select', options: ['New install', 'Replace existing', 'Repair', 'Not sure'] },
  ],
  painting: [
    { id: 'paint_area', label: 'Interior or exterior?', type: 'select', options: ['Interior', 'Exterior', 'Both'] },
    { id: 'paint_size', label: 'Approximate area', type: 'text', placeholder: 'e.g. 3 rooms, or whole house exterior' },
    { id: 'paint_condition', label: 'Surface condition', type: 'text', placeholder: 'e.g. good, some patching needed, peeling' },
  ],
  flooring: [
    { id: 'floor_type', label: 'Flooring type', type: 'select', options: ['Hardwood', 'Laminate', 'Vinyl / LVP', 'Tile', 'Carpet', 'Not sure'] },
    { id: 'floor_size', label: 'Approximate area', type: 'text', placeholder: 'e.g. ~400 sq ft, 2 rooms' },
    { id: 'floor_scope', label: 'New install or replace?', type: 'select', options: ['New install', 'Replace existing', 'Repair'] },
  ],
  remodeling: [
    { id: 'remodel_project', label: 'Project type', type: 'text', placeholder: 'e.g. kitchen, bathroom, basement' },
    { id: 'remodel_rooms', label: 'Rooms / areas involved', type: 'text', placeholder: 'e.g. kitchen + dining' },
    { id: 'remodel_sqft', label: 'Approximate square footage', type: 'text', placeholder: 'e.g. ~150 sq ft, or not sure' },
    { id: 'remodel_timeline', label: 'Timeline flexibility', type: 'text', placeholder: 'e.g. flexible, by spring' },
  ],
  concrete: [
    { id: 'concrete_project', label: 'Project type', type: 'chips', options: ['Driveway', 'Patio', 'Sidewalk', 'Slab / foundation', 'Steps', 'Repair'] },
    { id: 'concrete_size', label: 'Approximate size', type: 'text', placeholder: 'e.g. ~600 sq ft, 2-car driveway' },
    { id: 'concrete_scope', label: 'New pour or repair?', type: 'select', options: ['New', 'Repair / replace', 'Not sure'] },
  ],
  patios: [
    { id: 'patio_type', label: 'Patio type', type: 'chips', options: ['Concrete slab', 'Pavers', 'Stone', 'Covered patio', 'Patio cover only', 'Screened enclosure'] },
    { id: 'patio_size', label: 'Approximate size', type: 'text', placeholder: 'e.g. 12x16, ~300 sq ft' },
    { id: 'patio_scope', label: 'New build or repair?', type: 'select', options: ['New build', 'Extend existing', 'Repair / resurface', 'Not sure'] },
  ],
  fencing: FENCE_DECK_QUESTIONS,
  pressure_washing: [
    { id: 'pw_what', label: 'What needs washing?', type: 'chips', options: ['Driveway / concrete', 'House siding', 'Deck / patio', 'Roof', 'Fence', 'Other'] },
    { id: 'pw_size', label: 'Approximate area', type: 'text', placeholder: 'e.g. ~1,000 sq ft, whole driveway' },
    { id: 'pw_freq', label: 'One-time or recurring?', type: 'select', options: ['One-time', 'Recurring'] },
  ],
  appliance_repair: [
    { id: 'appl_which', label: 'Which appliance?', type: 'chips', options: ['Refrigerator', 'Washer', 'Dryer', 'Dishwasher', 'Oven / range', 'Microwave', 'Other'] },
    { id: 'appl_brand', label: 'Brand (if known)', type: 'text', placeholder: 'e.g. Samsung, Whirlpool, LG' },
    { id: 'appl_problem', label: "What's it doing?", type: 'text', placeholder: 'e.g. not draining, not heating, leaking' },
  ],
  general_contractor: [
    { id: 'gc_project', label: 'Project type', type: 'text', placeholder: 'e.g. kitchen remodel, room addition, bathroom' },
    { id: 'gc_rooms', label: 'Rooms / areas involved', type: 'text', placeholder: 'e.g. kitchen + dining' },
    { id: 'gc_sqft', label: 'Approximate square footage', type: 'text', placeholder: 'e.g. ~200 sq ft, or not sure' },
    { id: 'gc_timeline', label: 'Timeline flexibility', type: 'text', placeholder: 'e.g. flexible, by spring' },
  ],
  windows_doors: [
    { id: 'wd_which', label: 'Windows, doors, or both?', type: 'select', options: ['Windows', 'Doors', 'Both'] },
    { id: 'wd_count', label: 'How many?', type: 'text', placeholder: 'e.g. 6 windows, 1 exterior door' },
    { id: 'wd_scope', label: 'New install or replace?', type: 'select', options: ['New install', 'Replace existing', 'Repair'] },
  ],
  fence_deck: FENCE_DECK_QUESTIONS,
  handyman: [
    { id: 'handy_task', label: 'Type of task', type: 'chips', options: ['Mounting / hanging', 'Furniture assembly', 'Drywall / patching', 'Door / lock', 'Small repair', 'Multiple small jobs'] },
    { id: 'handy_count', label: 'Roughly how many tasks?', type: 'select', options: ['1', '2-3', '4+'] },
    { id: 'handy_details', label: 'Details', type: 'text', placeholder: 'e.g. mount 2 TVs, fix a sticking door' },
  ],

  // --- Outdoor & property ---
  landscaping: [
    { id: 'land_service', label: 'Service needed', type: 'chips', options: ['Design / new', 'Planting', 'Mulch / beds', 'Sod', 'Hardscape (pavers/stone)', 'Cleanup'] },
    { id: 'land_size', label: 'Yard size', type: 'text', placeholder: 'e.g. front yard, ~1/4 acre' },
    { id: 'land_freq', label: 'One-time or ongoing?', type: 'select', options: ['One-time', 'Ongoing'] },
  ],
  lawn_care: [
    { id: 'lawn_service', label: 'Service needed', type: 'chips', options: ['Mowing', 'Cleanup / overgrowth', 'Landscaping / design', 'Sod / new grass', 'Tree / shrub work', 'Irrigation'] },
    { id: 'lawn_size', label: 'Yard size', type: 'text', placeholder: 'e.g. small front yard, ~1/4 acre' },
    { id: 'lawn_frequency', label: 'One-time or recurring?', type: 'select', options: ['One-time', 'Recurring'] },
  ],
  tree_service: [
    { id: 'tree_service', label: 'Service needed', type: 'chips', options: ['Removal', 'Trimming / pruning', 'Stump grinding', 'Storm cleanup', 'Health / disease'] },
    { id: 'tree_count', label: 'How many trees?', type: 'text', placeholder: 'e.g. 1 large oak, 3 small' },
    { id: 'tree_access', label: 'Near power lines or structures?', type: 'select', options: ['Yes', 'No', 'Not sure'], helperText: 'This affects access and safety planning.' },
  ],
  pool_service: [
    { id: 'pool_service', label: 'Service needed', type: 'chips', options: ['Cleaning / maintenance', 'Repair', 'Opening / closing', 'Equipment', 'Resurfacing'] },
    { id: 'pool_type', label: 'Pool type', type: 'select', options: ['In-ground', 'Above-ground', 'Not sure'] },
    { id: 'pool_freq', label: 'One-time or recurring?', type: 'select', options: ['One-time', 'Recurring'] },
  ],

  // --- Cleaning & removal ---
  house_cleaning: [
    { id: 'clean_type', label: 'Type of cleaning', type: 'chips', options: ['Standard / recurring', 'Deep clean', 'Move-in / move-out', 'Post-construction'] },
    { id: 'clean_size', label: 'Home size', type: 'text', placeholder: 'e.g. 3 bed / 2 bath, ~1,800 sq ft' },
    { id: 'clean_freq', label: 'How often?', type: 'select', options: ['One-time', 'Weekly', 'Bi-weekly', 'Monthly'] },
  ],
  carpet_cleaning: [
    { id: 'carpet_areas', label: 'Areas to clean', type: 'text', placeholder: 'e.g. 3 rooms + hallway, stairs' },
    { id: 'carpet_what', label: 'Carpet or upholstery?', type: 'chips', options: ['Carpet', 'Rugs', 'Upholstery', 'Stairs'] },
    { id: 'carpet_stains', label: 'Any stains or pet odor?', type: 'text', placeholder: 'e.g. pet stains, high-traffic areas' },
  ],
  pest_control: [
    { id: 'pest_type', label: 'Pest type', type: 'chips', options: ['Ants', 'Roaches', 'Rodents', 'Termites', 'Mosquitoes', 'Bed bugs', 'Other'] },
    { id: 'pest_where', label: 'Inside, outside, or both?', type: 'select', options: ['Inside', 'Outside', 'Both'] },
    { id: 'pest_freq', label: 'One-time or recurring?', type: 'select', options: ['One-time', 'Recurring'] },
  ],
  junk_removal: [
    { id: 'junk_what', label: 'What needs removing?', type: 'text', placeholder: 'e.g. old couch, garage cleanout, appliances' },
    { id: 'junk_volume', label: 'Approximate volume', type: 'select', options: ['A few items', 'Half a truckload', 'Full truckload', 'Multiple loads'] },
    { id: 'junk_heavy', label: 'Any heavy / bulky items?', type: 'text', placeholder: 'e.g. piano, hot tub, fridge' },
  ],
  mold_remediation: [
    { id: 'mold_where', label: 'Where is the mold?', type: 'text', placeholder: 'e.g. bathroom ceiling, crawl space, behind drywall' },
    { id: 'mold_area', label: 'Approximate affected area', type: 'select', options: ['Small patch (under 10 sq ft)', '10-50 sq ft', 'Larger than 50 sq ft', 'Not sure'] },
    { id: 'mold_cause', label: 'Known water source?', type: 'text', placeholder: 'e.g. roof leak, burst pipe, humidity, unknown' },
    { id: 'mold_tested', label: 'Has it been tested or inspected?', type: 'select', options: ['Yes', 'No', 'Not sure'] },
  ],
  water_damage_restoration: [
    { id: 'water_source', label: 'Source of the water', type: 'chips', options: ['Burst / leaking pipe', 'Roof leak', 'Storm / flood', 'Appliance overflow', 'Sewage backup', 'Unknown'] },
    { id: 'water_when', label: 'When did it happen?', type: 'select', options: ['Happening now', 'Within 24 hours', 'A few days ago', 'A week or more'], helperText: 'For active flooding, shut off the water and call an emergency service line.' },
    { id: 'water_area', label: 'Affected areas', type: 'text', placeholder: 'e.g. living room + hallway, ~400 sq ft' },
    { id: 'water_insurance', label: 'Filing through insurance?', type: 'select', options: ['Yes', 'No', 'Not sure'] },
  ],

  // --- Pet services ---
  dog_training: [
    { id: 'dog_info', label: "Dog's breed & age", type: 'text', placeholder: 'e.g. 1-year-old Lab' },
    { id: 'dog_goals', label: 'What do you want to work on?', type: 'chips', options: ['Basic obedience', 'Leash manners', 'Potty training', 'Aggression / reactivity', 'Puppy basics', 'Tricks'] },
    { id: 'dog_format', label: 'Training format', type: 'select', options: ['In-home', 'Group classes', 'Board & train', 'No preference'] },
  ],
  pet_grooming: [
    { id: 'groom_pet', label: 'Pet type & breed', type: 'text', placeholder: 'e.g. Goldendoodle, domestic cat' },
    { id: 'groom_services', label: 'Services needed', type: 'chips', options: ['Bath', 'Haircut / trim', 'Nails', 'De-shedding', 'Ears / teeth'] },
    { id: 'groom_size', label: 'Size', type: 'select', options: ['Small', 'Medium', 'Large', 'Extra large'] },
  ],
  pet_sitting: [
    { id: 'sit_pets', label: 'Pet(s)', type: 'text', placeholder: 'e.g. 1 dog, 2 cats' },
    { id: 'sit_service', label: 'Service', type: 'select', options: ['Drop-in visits', 'Dog walking', 'Overnight / house sitting'] },
    { id: 'sit_dates', label: 'Dates needed', type: 'text', placeholder: 'e.g. June 20-25' },
  ],
  pet_boarding: [
    { id: 'board_pets', label: 'Pet(s) & size', type: 'text', placeholder: 'e.g. 1 large dog, 2 cats' },
    { id: 'board_dates', label: 'Dates needed', type: 'text', placeholder: 'e.g. June 20-25' },
    { id: 'board_needs', label: 'Any special needs?', type: 'text', placeholder: 'e.g. medication, special diet' },
  ],
  veterinary: [
    { id: 'vet_pet', label: 'Pet type & age', type: 'text', placeholder: 'e.g. 5-year-old cat' },
    { id: 'vet_reason', label: 'Reason for visit', type: 'chips', options: ['Wellness / checkup', 'Vaccinations', 'Illness / injury', 'Dental', 'Spay / neuter', 'Other'] },
    { id: 'vet_urgent', label: 'Is it urgent?', type: 'select', options: ['Yes', 'No'], helperText: 'For an emergency, please contact an emergency vet directly.' },
  ],

  // --- Professional & personal ---
  legal: [
    { id: 'legal_type', label: 'Type of matter', type: 'chips', options: ['Family', 'Estate / will', 'Business', 'Real estate', 'Personal injury', 'Criminal', 'Other'] },
    { id: 'legal_situation', label: 'Brief situation', type: 'text', placeholder: 'e.g. drafting a will, contract review' },
    { id: 'legal_deadline', label: 'Any deadline?', type: 'text', placeholder: 'e.g. court date, or none' },
  ],
  accounting: [
    { id: 'acct_service', label: 'Service needed', type: 'chips', options: ['Tax prep', 'Bookkeeping', 'Payroll', 'Business consulting', 'Other'] },
    { id: 'acct_who', label: 'Personal or business?', type: 'select', options: ['Personal', 'Business'] },
    { id: 'acct_deadline', label: 'Any deadline?', type: 'text', placeholder: 'e.g. tax deadline, or flexible' },
  ],
  insurance: [
    { id: 'ins_type', label: 'Type of insurance', type: 'chips', options: ['Auto', 'Home', 'Life', 'Health', 'Business', 'Other'] },
    { id: 'ins_need', label: 'New policy or review existing?', type: 'select', options: ['New policy', 'Review / compare', 'Claim help'] },
    { id: 'ins_notes', label: 'Anything specific?', type: 'text', placeholder: 'e.g. bundling auto + home' },
  ],
  real_estate: [
    { id: 're_action', label: 'Buying, selling, or renting?', type: 'select', options: ['Buying', 'Selling', 'Renting', 'Not sure'] },
    { id: 're_type', label: 'Property type', type: 'chips', options: ['House', 'Condo', 'Land', 'Commercial', 'Other'] },
    { id: 're_area', label: 'Area / timeline', type: 'text', placeholder: 'e.g. Denham Springs, within 3 months' },
  ],
  moving: [
    { id: 'move_type', label: 'Move type', type: 'select', options: ['Local', 'Long-distance', 'Within building', 'Storage'] },
    { id: 'move_size', label: 'Home size', type: 'text', placeholder: 'e.g. 2-bed apartment, 4-bed house' },
    { id: 'move_date', label: 'Moving date', type: 'text', placeholder: 'e.g. June 28, or flexible' },
    { id: 'move_packing', label: 'Need packing help?', type: 'select', options: ['Yes', 'No', 'Not sure'] },
  ],
  catering: [
    { id: 'cater_event', label: 'Event type', type: 'text', placeholder: 'e.g. wedding, office lunch, birthday' },
    { id: 'cater_guests', label: 'Guest count', type: 'text', placeholder: 'e.g. ~50 people' },
    { id: 'cater_when', label: 'Date & location', type: 'text', placeholder: 'e.g. June 30, Baton Rouge' },
    { id: 'cater_food', label: 'Food preferences / restrictions', type: 'text', placeholder: 'e.g. BBQ, vegetarian options' },
  ],
  photography: [
    { id: 'photo_type', label: 'Type of shoot', type: 'chips', options: ['Wedding', 'Portrait / family', 'Event', 'Real estate', 'Product', 'Other'] },
    { id: 'photo_when', label: 'Date & location', type: 'text', placeholder: 'e.g. July 12, City Park' },
    { id: 'photo_length', label: 'How long / coverage?', type: 'text', placeholder: 'e.g. 2 hours, full day' },
  ],
  tutoring: [
    { id: 'tutor_subject', label: 'Subject(s)', type: 'text', placeholder: 'e.g. algebra, ACT prep, reading' },
    { id: 'tutor_level', label: 'Student level', type: 'select', options: ['Elementary', 'Middle school', 'High school', 'College', 'Adult'] },
    { id: 'tutor_mode', label: 'In-person or online?', type: 'select', options: ['In-person', 'Online', 'Either'] },
    { id: 'tutor_freq', label: 'How often?', type: 'select', options: ['One-time', 'Weekly', 'A few times a week'] },
  ],

  // --- Backstop for "Other"/new/unmapped categories ---
  _generic: [
    { id: 'gen_scope', label: 'Approximate size or scope', type: 'text', placeholder: 'e.g. dimensions, # of rooms, or amount of work' },
    { id: 'gen_requirements', label: 'Specific requirements or preferences', type: 'text', placeholder: 'e.g. brand, materials, or any specifics' },
    { id: 'gen_access', label: 'Access or scheduling notes', type: 'text', placeholder: 'e.g. gate code, stairs, preferred days', helperText: 'Anything that helps the contractor plan.' },
  ],
};

// Static, qualitative guidance shown once per category above the guided
// questions. Deliberately non-numeric: it names what a contractor will ask
// for, never what the work costs. Partial<> by design -- only categories with
// reviewed copy appear here; the rest render no guidance line at all.
export const CATEGORY_GUIDANCE: Partial<Record<Category | '_generic', string>> = {
  roofing:
    'Good roofers usually need roof type, approximate size, number of stories, visible damage, insurance status, and photos before giving a firm price.',
  windows_doors:
    'Contractors usually need to know whether this is door-only, frame, sidelight, material, size, painting, and photos.',
  plumbing:
    'Plumbers usually need to know the fixture/location, whether water is leaking now, access, age of the unit, and whether this is urgent.',
};
